import { F, T } from '@/helpers'
import {
  CartPaymentMethodEnum,
  PurchasePaymentChannelEnum,
  api,
  type ICartConfirmPurchaseInfo,
} from '@setplex/tria-api'
import { createEvent, createStore, restore, sample, type Event } from 'effector'
import { model as genericPaymentModel } from '~/features/payment/generic'
import type {
  ApiError,
  ArgumentError,
  GravyTransactionFields,
  Option,
  TransactionCancelled,
  TransactionCreated,
  TransactionFailed,
} from '~/features/payment/interfaces/gravy'

/******* Form fields ********/

const formatTransactionFields = (payload: ICartConfirmPurchaseInfo) => {
  const gravy = payload.payment.gr4vyEmbedOptions
  if (!gravy) return null
  return {
    gr4vyId: gravy.gr4vyId,
    environment: gravy.environment,
    amount: gravy.amount,
    currency: gravy.currency,
    country: gravy.country,
    token: gravy.token,
    buyerId: gravy.buyerId,
    intent: gravy.intent,
    externalIdentifier: gravy.externalIdentifier,
    metadata: gravy.metadata,
    purchaseNumber: gravy.purchaseNumber,
  }
}

enum gravyPaymentsMethods {
  'applepay' = 'applepay',
  'googlepay' = 'googlepay',
  'paypal' = 'paypal',
  'redirect' = 'redirect',
  'multipago' = 'multipago',
}

const VALID_GRAVY_PAYMENTS_METHODS = Object.keys(gravyPaymentsMethods)

export const $transactionFields = //
  createStore<GravyTransactionFields | null>(null)
    .on(api.cart.confirmCartPurchaseFx.doneData, (_, payload) =>
      formatTransactionFields(payload)
    )
    .reset(genericPaymentModel.closePaymentPopup)

// Saves the latest purchase number to finish payment transaction
export const $lastPurchaseNumber = //
  createStore<string | null>(null).on(
    $transactionFields,
    (state, payload) => payload?.purchaseNumber || state
  )

interface SkinArgs<T> {
  '<args>': T[]
}

export const onOptionsLoaded = createEvent<Option[]>()
export const onOptionsLoadedRaw: Event<SkinArgs<Option[]>> =
  onOptionsLoaded.prepend((data) => data['<args>'][0])

// Gravy issue - event not fully synced to iframe state - in case of cancelation
export const onPaymentMethodSelected = createEvent<Option>()
const $currentPaymentMethod = restore(onPaymentMethodSelected, null)
export const onPaymentMethodSelectedRaw: Event<SkinArgs<Option>> =
  onPaymentMethodSelected.prepend((data) => data['<args>'][0])

export const onArgumentError = createEvent<ArgumentError>()
export const onArgumentErrorRaw: Event<SkinArgs<ArgumentError>> =
  onArgumentError.prepend((data) => data['<args>'][0])

export const onFormUpdate = createEvent<boolean>()
const $isFormValid = restore(onFormUpdate, false).reset(onPaymentMethodSelected)
export const onFormUpdateRaw: Event<SkinArgs<boolean>> = onFormUpdate.prepend(
  (data) => data['<args>'][0]
)

export const onTransactionCreated = createEvent<TransactionCreated>()
export const onTransactionCreatedRaw: Event<SkinArgs<TransactionCreated>> =
  onTransactionCreated.prepend((data) => data['<args>'][0])

export const onTransactionFailed = createEvent<TransactionFailed>()
export const onTransactionFailedRaw: Event<SkinArgs<TransactionFailed>> =
  onTransactionFailed.prepend((data) => data['<args>'][0])
export const onTransactionFailedFiltered = createEvent<TransactionFailed>()

export const onTransactionCancelled = createEvent<TransactionCancelled>()
export const onTransactionCancelledRaw: Event<SkinArgs<TransactionCancelled>> =
  onTransactionCancelled.prepend((data) => data['<args>'][0])

export const onApiError = createEvent<ApiError>()
export const onApiErrorRaw: Event<SkinArgs<ApiError>> = onApiError.prepend(
  (data) => data['<args>'][0]
)

export const onGravyRequestSended = createEvent()
export const $isPending = createStore<boolean>(false)
  .on(onGravyRequestSended, T)
  .reset([
    onApiError,
    onTransactionCancelled,
    onTransactionFailed,
    onTransactionCreated,
  ])

sample({
  clock: $isPending,
  target: genericPaymentModel.setSpecialRequestIsPending,
})

export const $isReadyToSubmit = createStore<boolean>(false)
  .on(
    [
      api.purchases.initializePurchaseProcessFx.pending,
      onTransactionCancelledRaw,
      onApiError,
      onTransactionCancelled,
      onTransactionFailedFiltered,
      onTransactionCreated,
      onGravyRequestSended,
    ],
    F
  )
  .on(api.purchases.initializePurchaseProcessFx.finally, T)

sample({
  clock: [$currentPaymentMethod, $isFormValid],
  source: {
    formValid: $isFormValid,
    currentPaymentMethod: $currentPaymentMethod,
  },
  fn: ({ formValid, currentPaymentMethod }) => {
    return Boolean(
      currentPaymentMethod?.method === gravyPaymentsMethods.multipago || // Multipago fix
        VALID_GRAVY_PAYMENTS_METHODS.includes(
          currentPaymentMethod?.mode || ''
        ) || // payment systems // this prevents confirm of untested methods
        (currentPaymentMethod?.mode === 'card' && currentPaymentMethod?.id) || // saved card
        (Boolean(currentPaymentMethod) && formValid) // new card
    )
  },
  target: $isReadyToSubmit,
})

// event with error message from gravy form - only async errors
export const gravyRequestError = createEvent<string>()

// Handle gravy request errors
sample({
  clock: gravyRequestError,
  target: genericPaymentModel.$paymentError,
})

const prettifyErrorMessage = (message?: string): string => {
  if (!message) return ''

  switch (message) {
    case 'Request failed validation':
      return 'Please validate fields'
    default:
      return message
  }
}

sample({
  clock: [onApiError, onTransactionFailed],
  fn: (clockData) => prettifyErrorMessage(clockData?.message),
  target: gravyRequestError,
})

sample({
  clock: onArgumentError,
  fn: (clockData) => `${clockData.option} ${clockData.message}`,
  target: gravyRequestError,
})
/******* Form fields ********/

sample({
  clock: onTransactionCreated,
  source: {
    purchaseNumber: $lastPurchaseNumber,
  },
  fn: ({ purchaseNumber }, clockData) => ({
    payment: {
      channel: PurchasePaymentChannelEnum.GR4VY_GATEWAY,
      method: CartPaymentMethodEnum.CREDIT_CARD,
      gr4vyPayRequest: {
        purchaseNumber: purchaseNumber || '',
        transactionId: clockData.id,
        transactionStatus: clockData.status,
        paymentMethodId: clockData.paymentMethod.id,
      },
    },
  }),
  target: api.purchases.initializePurchaseProcessFx,
})

/************ MULTIPAGO **********/
sample({
  // this filters out not correct Multipago events
  clock: onTransactionFailed,
  source: { currentPaymentMethod: $currentPaymentMethod },
  filter: ({ currentPaymentMethod }) =>
    currentPaymentMethod?.method !== gravyPaymentsMethods.multipago,
  fn: (_, clockData) => clockData,
  target: onTransactionFailedFiltered,
})

sample({
  // this enables submit button for Multipago as theris not isValid event on this form
  clock: onPaymentMethodSelected,
  filter: (clockData) => clockData?.id === gravyPaymentsMethods.multipago,
  fn: T,
  target: $isReadyToSubmit,
})
/************ MULTIPAGO **********/
